/**
 * Annotation System – Type Definitions
 * Potree Point Cloud Viewer v3.0
 */

// ─── Primitives ────────────────────────────────────────────────────────────────

export interface Vec3 {
  x: number
  y: number
  z: number
}

// ─── Annotation ─────────────────────────────────────────────────────────────────

export type AnnotationType =
  | 'position_marker'       // Positionsmarker
  | 'construction_detail'   // Konstruktionsdetail
  | 'material_property'     // Materialeigenschaft
  | 'diagnostics'           // Diagnostik
  | 'pollutant'             // Schadstoff
  | 'core_drill'            // Kernbohrung
  | 'measurement'           // Messung

export const ANNOTATION_TYPE_LABELS: Record<AnnotationType, string> = {
  position_marker:     'Positionsmarker',
  construction_detail: 'Konstruktionsdetail',
  material_property:   'Materialeigenschaft',
  diagnostics:         'Diagnostik',
  pollutant:           'Schadstoff',
  core_drill:          'Kernbohrung',
  measurement:         'Messung',
}

export const ANNOTATION_TYPE_COLORS: Record<AnnotationType, string> = {
  position_marker:     '#4a8fe8',  // blau — räumliche Markierung
  construction_detail: '#a84ae8',  // lila — Baudetail
  material_property:   '#e6a832',  // gelb — Materialkunde
  diagnostics:         '#4ae88a',  // grün — Untersuchung
  pollutant:           '#e63232',  // rot — Schadstoff / Gefahr
  core_drill:          '#ff8c00',  // orange — Kernbohrung
  measurement:         '#3b82f6',  // blau-500 — Messung
}

/** Kamera-Zustand für eine Annotation (Potree-Koordinaten) */
export interface AnnotationCamera {
  position: Vec3
  target: Vec3
  /** Optionale Flugdauer in ms (Standard: 800) */
  animationDuration?: number
}

/** Ein einzelner 3D-Punkt in der Punktwolke */
export interface Annotation {
  id: string
  /** Kurzer Anzeigetitel (max. ~60 Zeichen) */
  title: string
  /** Kurztext für Tooltip / Popup-Vorschau (max. ~200 Zeichen) */
  tooltip: string
  /**
   * Vollständiger Bodytext als TipTap JSON-Dokument.
   * Kann Bilder, Links, Video-Embeds und AnnotationLink-Nodes enthalten.
   * Leer-Dokument: `{ type: 'doc', content: [] }`
   */
  body: TipTapDocument
  type: AnnotationType
  tags: string[]
  /**
   * 3D-Position des Markers in Weltkoordinaten der Punktwolke.
   * Wird für die Projektion auf den Bildschirm verwendet.
   */
  position: Vec3
  /** Kamera-Zustand, der beim Ansteuern dieser Annotation gesetzt wird */
  camera: AnnotationCamera
  /** ID der Punktwolke, zu der diese Annotation gehört */
  cloudId: string
  /** Ob das Popup bei Aktivierung (Klick) angezeigt werden soll */
  showPopup: boolean
  /** IDs verknüpfter Paperless-Dokumente */
  paperlessDocIds: number[]
  /** Mittleres Thumbnail (~320px, data:image/jpeg;base64,…) */
  thumbnailMedium?: string
  /** Großes Thumbnail (~640px, data:image/jpeg;base64,…) */
  thumbnailHigh?: string
  createdAt: string  // ISO 8601
  updatedAt: string  // ISO 8601
}

// ─── TipTap JSON ─────────────────────────────────────────────────────────────

/**
 * Vereinfachtes TipTap-Dokument-Schema.
 * Das vollständige Schema wird von @tiptap/core abgeleitet.
 */
export interface TipTapDocument {
  type: 'doc'
  content: TipTapNode[]
}

export interface TipTapNode {
  type: string
  attrs?: Record<string, unknown>
  content?: TipTapNode[]
  marks?: TipTapMark[]
  text?: string
}

export interface TipTapMark {
  type: string
  attrs?: Record<string, unknown>
}

/** Leeres TipTap-Dokument als Initialzustand */
export const EMPTY_TIPTAP_DOC: TipTapDocument = {
  type: 'doc',
  content: [],
}

// ─── Document ────────────────────────────────────────────────────────────────

/**
 * Ein Rich-Text-Dokument, das Annotationen verlinken kann.
 * Beim Scrollen durch das Dokument werden die verknüpften Annotationen
 * im 3D-Raum angesteuert.
 */
export interface AnnotationDocument {
  id: string
  title: string
  /**
   * TipTap JSON-Dokument.
   * Enthält `annotationLink`-Nodes, die auf Annotation-IDs verweisen.
   */
  content: TipTapDocument
  /** Alle in diesem Dokument referenzierten Annotationen (denormalisiert für schnellen Zugriff) */
  linkedAnnotationIds: string[]
  /** ID der zugehörigen Punktwolke (optional – ein Dokument kann übergreifend sein) */
  cloudId?: string
  createdAt: string
  updatedAt: string
}

// ─── Building Data (Gebäudedaten) ────────────────────────────────────────────

export interface BuildingDataField {
  key: string
  label: string
  hint: string            // z.B. "gemäß HOAI, Anlage 10.2"
  type: 'text' | 'select'
  options?: string[]      // nur bei type === 'select'
  unit?: string           // z.B. 'm²', 'm³' — wird rechts im Feld angezeigt
  required: boolean
}

export interface BuildingData {
  projectId?: string
  fields: Record<string, string>   // key → Wert
  lat?: number | null              // Geocoded Breitengrad
  lon?: number | null              // Geocoded Längengrad
  updatedAt: string
}

export const BUILDING_DATA_SCHEMA: BuildingDataField[] = [
  { key: 'bauwerkskategorie', label: 'Bauwerkskategorie', hint: 'gemäß HOAI, Anlage 10.2', type: 'select', options: [
    'Wohnhäuser',
    'Siedlungen',
    'Wohnanlagen',
    'Büro- und Verwaltungsgebäude',
    'Banken und Sparkassen',
    'Gebäude für Handel und Verkauf',
    'Gaststätten und Hotels',
    'Kindergärten und Horte',
    'Allgemeinbildende Schulen',
    'Berufsbildende Schulen',
    'Hochschulen und Universitäten',
    'Wissenschaft und Forschung',
    'Bibliotheken und Archive',
    'Sport- und Mehrzweckhallen',
    'Schwimmbäder und Hallenbäder',
    'Krankenhäuser und Kliniken',
    'Pflegeheime und Senioreneinrichtungen',
    'Sakralbauten',
    'Kulturbauten (Museen, Theater)',
    'Justizvollzugsanstalten',
    'Feuerwehr- und Rettungswachen',
    'Produktions- und Industriegebäude',
    'Lagerhallen und Logistikgebäude',
    'Landwirtschaftliche Betriebsgebäude',
    'Parkhäuser und Tiefgaragen',
  ], required: true },
  { key: 'ort', label: 'Ort', hint: 'Straße, Ort, PLZ', type: 'text', required: true },
  { key: 'bauweise', label: 'Bauweise', hint: '', type: 'select', options: ['Monolithische Bauweise', 'Fertigteilbauweise', 'Mischbauweise'], required: true },
  { key: 'baujahr', label: 'Baujahr', hint: 'Jahreszahl', type: 'text', required: true },
  { key: 'gebaeudeklasse', label: 'Gebäudeklasse', hint: 'gemäß MBO § 2 Abs. 3', type: 'select', options: [
    'GK 1a – freistehend, ≤ 7 m, ≤ 2 NE, ≤ 400 m²',
    'GK 1b – freistehend, land-/forstwirtschaftlich',
    'GK 2 – ≤ 7 m, ≤ 2 NE, ≤ 400 m²',
    'GK 3 – sonstige Gebäude, ≤ 7 m',
    'GK 4 – ≤ 13 m, NE je ≤ 400 m²',
    'GK 5 – sonstige Gebäude / Unterirdische',
  ], required: true },
  { key: 'bri', label: 'BRI', hint: 'Brutto-Rauminhalt', type: 'text', unit: 'm³', required: true },
  { key: 'bgf', label: 'BGF', hint: 'Brutto-Grundfläche', type: 'text', unit: 'm²', required: true },
  { key: 'nutzungsart', label: 'Nutzungsart', hint: 'gemäß HOAI, Anlage 10.2', type: 'select', options: [
    'Wohnen',
    'Büro und Verwaltung',
    'Handel und Verkauf',
    'Gewerbe und Industrie',
    'Bildung und Wissenschaft',
    'Gesundheit und Pflege',
    'Kultur und Veranstaltung',
    'Sport und Freizeit',
    'Gastronomie und Beherbergung',
    'Sakral und religiös',
    'Verkehr und Infrastruktur',
    'Landwirtschaft',
    'Lager und Logistik',
    'Sicherheit und Justiz',
    'Mischnutzung',
  ], required: true },
  { key: 'umbaumassnahmen', label: 'Umbaumaßnahmen', hint: 'vergangene Eingriffe', type: 'text', required: true },
  { key: 'gebaeudekonstruktion', label: 'Konstruktion', hint: 'Tragwerk, Bauart', type: 'text', required: true },
  { key: 'art_des_gebaeudes', label: 'Gebäudeart', hint: 'gemäß HOAI, Anlage 10.2', type: 'select', options: [
    'Einfamilienhaus',
    'Doppelhaushälfte',
    'Reihenhaus',
    'Mehrfamilienhaus',
    'Wohnhochhaus',
    'Bürogebäude',
    'Verwaltungsgebäude',
    'Geschäftshaus',
    'Kaufhaus / Warenhaus',
    'Schulgebäude',
    'Kindergarten / Kindertagesstätte',
    'Hochschulgebäude',
    'Bibliothek',
    'Krankenhausgebäude',
    'Pflegeheim',
    'Kirche / Sakralbau',
    'Museum / Ausstellungsgebäude',
    'Theater / Konzertgebäude',
    'Sporthalle / Schwimmhalle',
    'Hotel / Gasthaus',
    'Produktionshalle',
    'Werkstattgebäude',
    'Lagerhalle',
    'Parkhaus / Tiefgarage',
    'Feuerwache / Rettungsstation',
    'Landwirtschaftliches Gebäude',
  ], required: true },
  { key: 'nutzungsgeschichte', label: 'Nutzungsgeschichte', hint: 'frühere Nutzungen', type: 'text', required: true },
  { key: 'erschliessung', label: 'Erschließung', hint: 'z. B. Fahrstuhl, Einrüstung', type: 'text', required: true },
]

export const EMPTY_BUILDING_DATA: BuildingData = {
  fields: {},
  lat: null,
  lon: null,
  updatedAt: new Date().toISOString(),
}

// ─── Store Root ──────────────────────────────────────────────────────────────

/** Persistiertes Datenmodell in `annotations.json` */
export interface AnnotationsData {
  version: 1
  annotations: Annotation[]
  documents: AnnotationDocument[]
  buildingData?: BuildingData
}

export const EMPTY_ANNOTATIONS_DATA: AnnotationsData = {
  version: 1,
  annotations: [],
  documents: [],
}

// ─── UI State ────────────────────────────────────────────────────────────────

export type EditorMode =
  | 'none'
  | 'place'       // Benutzer platziert eine neue Annotation (Klick in die Wolke)
  | 'relocate'    // Benutzer verschiebt eine bestehende Annotation (Klick in die Wolke)
  | 'edit'        // Annotation wird im Editor bearbeitet
  | 'view'        // Annotation wird im Lesemodus angezeigt

export interface AnnotationEditorState {
  mode: EditorMode
  activeAnnotationId: string | null
  activeDocumentId: string | null
  /** Vorläufige 3D-Position während der Platzierung */
  pendingPosition: Vec3 | null
}

// ─── View Snapshot ──────────────────────────────────────────────────────────

/**
 * Speichert einen vollständigen Viewer-Zustand:
 * Kamera (Position + Target) + ClipBox (Position, Skalierung, Rotation).
 * Wird als TipTap-Block-Node im Dokument eingebettet und beim Scrollen
 * angesteuert, um den exakten Ausschnitt der Punktwolke wiederherzustellen.
 */
export interface ViewSnapshot {
  /** Kamera-Position + Target */
  camera: AnnotationCamera
  /** ClipBox-Zustand (optional — wenn null, wird die ClipBox nicht verändert) */
  clipBox?: ClipBoxState | null
  /** IDs der zum Zeitpunkt der Aufnahme sichtbaren Punktwolken */
  activeCloudIds?: string[]
  /** Kurzbeschriftung für die Vorschau im Editor */
  label: string
  /** Zeitstempel der Aufnahme */
  capturedAt: string  // ISO 8601
  /** Base64-Thumbnail des Viewer-Canvas zum Zeitpunkt der Aufnahme */
  thumbnail?: string
}

/** Zustand der Potree ClipBox (position, scale, rotation als Arrays) */
export interface ClipBoxState {
  position: [number, number, number]
  scale:    [number, number, number]
  rotation: [number, number, number]  // Euler-Winkel (x, y, z)
}

// ─── Scene ───────────────────────────────────────────────────────────────────

/**
 * Eine Scene speichert einen definierten Viewer-Zustand (Kamera + ClipBox)
 * zusammen mit einer Auswahl von Notizen und Audits.
 */
export interface Scene {
  id: string
  title: string
  description?: string
  /** Projekt-ID, an die die Scene gebunden ist (neu, primär) */
  projectId?: string
  /** Cloud-ID — legacy, für Bestandsdaten */
  cloudId?: string
  /** Gespeicherter Viewer-Zustand */
  viewerState: SceneViewerState
  /** IDs der aktiven (sichtbaren) Punktwolken */
  activeCloudIds: string[]
  /** IDs der zugeordneten Annotationen (Notizen) */
  annotationIds: string[]
  /** IDs der zugeordneten Audits */
  auditIds: string[]
  createdAt: string  // ISO 8601
  updatedAt: string  // ISO 8601
  /** Ansichtsmodus: modell (3D), grundriss oder schnitt */
  viewMode?: 'modell' | 'grundriss' | 'schnitt'
  /** Name des Schnitts/Grundrisses (für grundriss/schnitt-Modes) */
  viewTargetName?: string
  /** Startscene: wird beim Laden der Session automatisch aktiviert */
  isStartScene?: boolean
  /** Kleines Thumbnail für Listen (~88×60px, data:image/jpeg;base64,…) */
  thumbnailSmall?: string
  /** Mittleres Thumbnail für Cards/Whiteboard (~320px, data:image/jpeg;base64,…) */
  thumbnailMedium?: string
  /** Großes Thumbnail für Whiteboard-Cards (~640px, data:image/jpeg;base64,…) */
  thumbnailHigh?: string
}

/** Kamera- und ClipBox-Zustand einer Scene */
export interface SceneViewerState {
  camera: AnnotationCamera
  clipBox?: ClipBoxState | null

  /** Globale Renderer-Einstellungen (Potree Viewer) */
  renderer?: SceneRendererSettings
  /** Darstellungseinstellungen pro Punktwolke */
  clouds?: SceneCloudSettings[]
  /** WMS-Datenlayer-Zustand */
  dataLayers?: SceneDataLayer[]
  /** Geo-Anker für Datenlayer-Ausrichtung */
  geoAnchor?: SceneGeoAnchor | null
}

/** Globale Potree-Renderer-Einstellungen */
export interface SceneRendererSettings {
  pointBudget?: number
  fov?: number
  edlEnabled?: boolean
  edlRadius?: number
  edlStrength?: number
  hqSplats?: boolean
  background?: string          // "gradient" | "black" | "white" | "skybox"
  minNodeSize?: number
}

/** Darstellungseinstellungen einer einzelnen Punktwolke */
export interface SceneCloudSettings {
  id: string                   // Cloud-ID
  visible: boolean
  opacity: number              // 0–1
  pointSize: number
  pointSizeType: number        // 0=fixed, 1=adaptive
  shape: number                // 0=square, 1=circle, 2=paraboloid
  activeAttribute: string      // "rgba" | "elevation" | "intensity" ...
  elevationRange?: [number, number] | null
  intensityRange?: [number, number] | null
  rgbGamma?: number
  rgbBrightness?: number
  rgbContrast?: number
  position?: { x: number; y: number; z: number }
  rotation?: { x: number; y: number; z: number }
  color?: string | null        // Manuelle Hex-Farbe
}

/** Zustand eines WMS-Datenlayers */
export interface SceneDataLayer {
  id: string                   // z.B. "alkis-gebaeude"
  visible: boolean
  filters: {
    opacity: number            // 0–1
    hue: number                // -180 bis 180
    brightness: number         // -1 bis 1
    contrast: number           // -1 bis 1
    saturate: number           // -1 bis 1
    invert: number             // 0 oder 1
  }
}

/** Geo-Anker für WMS-Layer-Ausrichtung */
export interface SceneGeoAnchor {
  originE: number
  originN: number
  rotation: number
  offsetX: number
  offsetY: number
  srs: string
}

/** Persistiertes Datenmodell in `scenes.json` */
export interface ScenesData {
  version: 1
  scenes: Scene[]
}

export const EMPTY_SCENES_DATA: ScenesData = {
  version: 1,
  scenes: [],
}

// ─── Scroll Navigation ────────────────────────────────────────────────────────

/**
 * Wird von ScrollNavigator emittiert, wenn eine Annotation sichtbar wird.
 */
export interface ScrollNavEvent {
  annotationId: string
  /** Normalisierte Scroll-Position im Dokument [0, 1] */
  progress: number
}
